import { useEffect, useMemo, useRef, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { PriceBenchMark } from '../../../store/PriceBenchMark';
import { usaCurrency } from '../../../utils/helpers';
import { SelectMenu } from '../../Atoms/SelectMenu';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';

type ProductFilter = 'DIA' | 'Broadband';

const TOOLTIP_TEXT =
  "Market averages are unique to your company's profile. They take into account the details of your services — such as region, product, supplier, and cost — and compare them against similar services in our Price Benchmark model.";

interface MarketValues {
  marketLow: number;
  marketHigh: number;
  youValue: number;
}

function computeMarketValues(benchmarks: PriceBenchMark[]): MarketValues {
  if (!benchmarks.length) {
    return { marketLow: 0, marketHigh: 0, youValue: 0 };
  }

  const avg = (arr: number[]) => {
    const valid = arr.filter((v) => !isNaN(v) && isFinite(v));
    return valid.length ? valid.reduce((s, v) => s + v, 0) / valid.length : 0;
  };

  const marketLow = avg(benchmarks.map((b) => Number(b.mrc) - Number(b.priceToLowest)));
  const marketHigh = avg(benchmarks.map((b) => Number(b.mrc) - Number(b.priceToHighest)));
  const youValue = avg(benchmarks.map((b) => Number(b.mrc)));

  return { marketLow, marketHigh, youValue };
}

// A benchmark has alt supplier predictions when the pricing model supplied non-zero deltas.
function hasAltSupplierData(b: PriceBenchMark): boolean {
  return b.priceToLowest !== 0 || b.priceToHighest !== 0;
}

// Used in minmax mode: low = cheapest service mrc, high = most expensive service mrc.
function computeMinMaxValues(benchmarks: PriceBenchMark[]): MarketValues {
  if (!benchmarks.length) {
    return { marketLow: 0, marketHigh: 0, youValue: 0 };
  }
  const { min, max, sum, count } = benchmarks.reduce(
    (acc, b) => {
      const v = Number(b.mrc);
      if (!isFinite(v)) return acc;
      return {
        min: v < acc.min ? v : acc.min,
        max: v > acc.max ? v : acc.max,
        sum: acc.sum + v,
        count: acc.count + 1
      };
    },
    { min: Infinity, max: -Infinity, sum: 0, count: 0 }
  );
  if (count === 0) {
    return { marketLow: 0, marketHigh: 0, youValue: 0 };
  }
  return {
    marketLow: min,
    marketHigh: max,
    youValue: sum / count
  };
}

function filterByProduct(benchmarks: PriceBenchMark[], product: ProductFilter): PriceBenchMark[] {
  return benchmarks.filter((b) => {
    const name = (b.product?.name || '').toLowerCase();
    const family = (b.product?.family || '').toLowerCase();
    const nameAbbreviation = (b.product?.nameAbbreviation || '').toLowerCase();
    const keyword = product.toLowerCase();
    return name.includes(keyword) || family.includes(keyword) || nameAbbreviation.includes(keyword);
  });
}

interface YouMarkerProps {
  pct: number;
  value: number;
}

function YouMarker({ pct, value }: YouMarkerProps) {
  const clampedPct = Math.max(0, Math.min(100, pct));
  const translateX = pct <= 0 ? 'translateX(0%)' : pct >= 100 ? 'translateX(-100%)' : 'translateX(-50%)';
  return (
    <div
      className="absolute -top-16 flex flex-col items-center"
      style={{ left: `${clampedPct}%`, transform: translateX }}>
      <div className="whitespace-nowrap px-4 py-3 rounded text-white bg-grey-9">You: {usaCurrency.format(value)}</div>
      <div className="w-1 h-7 bg-grey-9" />
    </div>
  );
}

interface SectionLabelProps {
  value: number;
  label: string;
}

function SectionLabel({ value, label }: SectionLabelProps) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="text-base text-grey-8">{usaCurrency.format(value)}</span>
      <span className="text-base text-grey-8">{label}</span>
    </div>
  );
}

interface MarketComparisonChartProps {
  benchmarks: PriceBenchMark[];
  loading?: boolean;
}

export function MarketComparisonChart({ benchmarks, loading = false }: MarketComparisonChartProps) {
  const [selectedProduct, setSelectedProduct] = useState<ProductFilter>(() => {
    const diaData = filterByProduct(benchmarks, 'DIA');
    const bbData = filterByProduct(benchmarks, 'Broadband');
    return diaData.length === 0 && bbData.length > 0 ? 'Broadband' : 'DIA';
  });

  const diaFiltered = useMemo(() => filterByProduct(benchmarks, 'DIA'), [benchmarks]);
  const broadbandFiltered = useMemo(() => filterByProduct(benchmarks, 'Broadband'), [benchmarks]);

  const diaHasData = diaFiltered.length > 0;
  const broadbandHasData = broadbandFiltered.length > 0;

  // Track selectedProduct in a ref so the auto-correction effect can read its
  // current value without re-running on every user selection.
  const selectedProductRef = useRef(selectedProduct);
  selectedProductRef.current = selectedProduct;

  useEffect(() => {
    if (selectedProductRef.current === 'DIA' && !diaHasData && broadbandHasData) {
      setSelectedProduct('Broadband');
    } else if (selectedProductRef.current === 'Broadband' && !broadbandHasData && diaHasData) {
      setSelectedProduct('DIA');
    }
  }, [diaHasData, broadbandHasData]);

  const filtered = selectedProduct === 'DIA' ? diaFiltered : broadbandFiltered;
  const withAlt = filtered.filter(hasAltSupplierData);

  // Chart display mode per the display rules:
  // 'original'     — ≥1 service has alt supplier data; use those (ignore the rest).
  // 'minmax'       — no alt supplier data at all, but ≥4 services; low/high = min/max mrc.
  // 'insufficient' — no alt supplier data and <4 services; show a message instead.
  const chartMode: 'original' | 'minmax' | 'insufficient' =
    withAlt.length > 0 ? 'original' : filtered.length >= 4 ? 'minmax' : 'insufficient';

  const effectiveData = chartMode === 'original' ? withAlt : filtered;
  const { marketLow, marketHigh, youValue } =
    chartMode === 'minmax' ? computeMinMaxValues(effectiveData) : computeMarketValues(effectiveData);

  const hasData = filtered.length > 0;
  const showChart = hasData && chartMode !== 'insufficient' && (chartMode === 'minmax' || marketHigh > 0);

  const lowLabel = chartMode === 'minmax' ? 'Lowest MRC' : 'Market Average Low';
  const highLabel = chartMode === 'minmax' ? 'Highest MRC' : 'Market Average High';

  const displayLow = Math.max(0, marketLow);
  const displayHigh = marketHigh;

  const totalSpan = displayHigh - displayLow;
  const midpointValue = (displayLow + displayHigh) / 2;

  // Market Low and Market High are always pinned 1/6 from each edge
  const posLow = 1 / 6;
  const posHigh = 5 / 6;
  // Midpoint sits exactly halfway between the two pinned anchors
  const posMid = (posLow + posHigh) / 2;

  // You marker: linear mapping from [displayLow, displayHigh] → [posLow, posHigh]
  const youPct =
    totalSpan <= 0
      ? 50
      : Math.max(0, Math.min(100, (posLow + ((youValue - displayLow) / totalSpan) * (posHigh - posLow)) * 100));

  return (
    <div className="px-6 pt-6 pb-12 mb-6 rounded border border-grey-2 bg-white">
      <div className="relative flex items-center justify-center mb-10">
        <div className="absolute left-0 w-28">
          <SelectMenu
            name="market-comparison-product"
            options={[
              { value: 'DIA', text: 'DIA', disabled: !diaHasData },
              { value: 'Broadband', text: 'Broadband', disabled: !broadbandHasData }
            ]}
            value={selectedProduct}
            selectClasses="text-xs"
            handleSelect={(e) => setSelectedProduct(e.target.value as ProductFilter)}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-md font-medium text-grey-6">Market Comparison</span>
          <Tooltip
            title={TOOLTIP_TEXT}
            arrow
            placement="right">
            <IconButton
              aria-label="Market comparison info"
              size="small"
              sx={{ p: 0, color: 'inherit' }}>
              <InfoIcon
                className="text-grey-4"
                sx={{ fontSize: 18 }}
              />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {loading ? (
        <ComponentSpinner sx={{ height: '5rem' }} />
      ) : !hasData ? (
        <div className="flex items-center justify-center h-20 text-grey-4 text-sm">
          No {selectedProduct} benchmark data available.
        </div>
      ) : !showChart ? (
        <div className="flex items-center justify-center h-20 text-grey-4 text-sm">
          Not enough alternative suppliers
        </div>
      ) : (
        <div className="relative mt-24">
          <YouMarker
            pct={youPct}
            value={youValue}
          />

          <div
            className="relative z-10 w-full h-8 rounded overflow-hidden"
            style={{ background: `linear-gradient(to right, #B8D936, #FCCE7E ${posMid * 100}%, #D96E2B)` }}
          />

          <div className="relative mt-3 h-14">
            <div
              className="absolute"
              style={{ left: `${posLow * 100}%`, transform: 'translateX(-50%)' }}>
              <SectionLabel
                value={displayLow}
                label={lowLabel}
              />
            </div>
            <div
              className="absolute"
              style={{ left: `${posMid * 100}%`, transform: 'translateX(-50%)' }}>
              <span className="text-base text-grey-8">{usaCurrency.format(midpointValue)}</span>
            </div>
            <div
              className="absolute"
              style={{ left: `${posHigh * 100}%`, transform: 'translateX(-50%)' }}>
              <SectionLabel
                value={displayHigh}
                label={highLabel}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
